import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DealCard } from '@/components/DealCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { getBestOfCategoryBySlug } from '@/contracts/bestOf';
import { useBestOfProducts } from '@/hooks/useBestOf';
import { ComparisonTableSection } from '@/components/bestof/ComparisonTableSection';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BestOf() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const navigate = useNavigate();
  const category = categorySlug ? getBestOfCategoryBySlug(categorySlug) : null;
  const { data: products, isLoading } = useBestOfProducts(category);

  if (!category) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
          <p className="text-muted-foreground mb-8">This best-of category doesn't exist yet.</p>
          <Button onClick={() => navigate('/browse')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Browse
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category.metaTitle}</title>
        <meta name="description" content={category.metaDescription} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-b from-muted/30 to-background py-12 border-b">
          <div className="container mx-auto px-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/browse')}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Browse
            </Button>
            
            <div className="flex items-start gap-3 mb-4">
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                <TrendingUp className="h-3 w-3 mr-1" />
                Best Of
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{category.h1}</h1>
            <p className="text-lg text-muted-foreground max-w-3xl">{category.introText}</p>
          </div>
        </section>

        {/* Ranked Product List */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold mb-8">Top Ranked Products</h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-[400px] rounded-lg" />
              ))}
            </div>
          ) : !products || products.length === 0 ? (
            <div className="text-center py-16 bg-muted/30 rounded-lg">
              <p className="text-muted-foreground">No products found for this category yet.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {products.map((product, index) => (
                  <div key={product.id} className="relative">
                    {/* Rank Badge */}
                    <div className="absolute -top-3 -left-3 z-10 bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                      #{index + 1}
                    </div>
                    <DealCard 
                      {...product}
                      circleScore={product.circleScore}
                    />
                  </div>
                ))}
              </div>

              {/* Comparison Table */}
              {products.length >= 3 && (
                <ComparisonTableSection products={products.slice(0, 10)} />
              )}
            </>
          )}
        </section>

        {/* FAQ Section */}
        <section className="bg-muted/30 py-12 border-t">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6 max-w-3xl">
              {category.faqItems.map((faq, index) => (
                <div key={index} className="bg-background p-6 rounded-lg border">
                  <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
